"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Download, TrendingUp, TrendingDown, Users } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { formatCurrency } from "@/lib/utils";

interface SubscriptionRevenueDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    summary: {
      totalSubscribers: number;
      activeSubscribers: number;
      newSubscriptions: number;
      cancelledSubscriptions: number;
      netSubscribers: number;
    };
    planBreakdown: Array<{
      plan: string;
      activeCount: number;
      price: number;
      revenue: number;
    }>;
    totalRevenue: number;
  };
}

const COLORS = ["#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981"];

export function SubscriptionRevenueDashboard({
  data,
}: SubscriptionRevenueDashboardProps) {
  const handleExport = () => {
    const exportData = data.planBreakdown.map((row) => ({
      "Subscription Plan": row.plan,
      "Active Subscriptions": row.activeCount,
      Price: row.price,
      Revenue: row.revenue,
    }));

    // Add totals
    exportData.push({
      "Subscription Plan": "TOTAL",
      "Active Subscriptions": data.summary.activeSubscribers,
      Price: 0,
      Revenue: data.totalRevenue,
    });

    exportToExcel(exportData, "Subscription_Revenue", "Revenue");
  };

  // Prepare data for pie chart
  const pieData = data.planBreakdown.map((item) => ({
    name: item.plan,
    value: item.revenue,
  }));

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Subscribers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.summary.totalSubscribers}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Active Subscribers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data.summary.activeSubscribers}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              New Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{data.summary.newSubscriptions}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-500" />
              Cancelled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              -{data.summary.cancelledSubscriptions}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Net Subscribers</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                data.summary.netSubscribers >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {data.summary.netSubscribers >= 0 ? "+" : ""}
              {data.summary.netSubscribers}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total Revenue Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950">
        <CardHeader>
          <CardTitle>Total Subscription Revenue</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-4xl font-bold text-blue-600">
            {formatCurrency(data.totalRevenue)}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Revenue from active subscriptions in the period
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Revenue by Plan Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Plan Breakdown Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Plan Breakdown</CardTitle>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.planBreakdown.map((row) => (
                  <TableRow key={row.plan}>
                    <TableCell className="font-medium">{row.plan}</TableCell>
                    <TableCell className="text-right">{row.activeCount}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(row.price)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(row.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50 font-bold">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right">
                    {data.summary.activeSubscribers}
                  </TableCell>
                  <TableCell className="text-right">-</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(data.totalRevenue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
